import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Flex,
    Box,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from "@chakra-ui/react";
import IconButton from "../../components/Theme/IconButton";
import Icon from "../../components/Theme/Icon";
import httpRequest from "../../core/services/http-request";
import moment from "moment";
import {useUser} from "../../core/hooks";
import AnswerMessageModal from "../Notifications/AnswerMessageModal";

const ChatModal = ({ isOpen, onClose, message, getUse }) => {
    const { post } = httpRequest();
    const { user } = useUser();

    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(true);

    const bodyRef = useRef(null);
    const pageRef = useRef(1);
    const fetchingRef = useRef(false);

    const { isOpen: isOpenNew, onOpen: onOpenNew, onClose: onCloseNew } = useDisclosure();

    const OLDEST_FIRST = true;

    const getChat = async (pageNumber = 1) => {
        if (fetchingRef.current) return;

        fetchingRef.current = true;
        setLoading(true);

        try {
            const targetId = message?.user?.id || message?.userId;

            const data = {
                compId: message?.actionId,
                userId: targetId,
                page: pageNumber,
            };

            const response = await post("get-chat", data);
            const newMessages = response?.data?.data?.items || [];

            if (!newMessages.length) {
                setHasMore(false);
                fetchingRef.current = false;
                setLoading(false);
                return;
            }

            pageRef.current = pageNumber;

            setMessages((prev) => {
                if (pageNumber === 1) return newMessages;
                return OLDEST_FIRST ? [...prev, ...newMessages] : [...newMessages, ...prev];
            });

            if (pageNumber === 1) {
                setTimeout(() => {
                    if (bodyRef.current) {
                        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
                    }
                }, 100);
            }

        } catch (err) {
            console.error("getChat error", err);
        } finally {
            fetchingRef.current = false;
            setLoading(false);
        }
    };

    const handleScroll = (e) => {
        const el = e.target;

        const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

        if (hasMore && !fetchingRef.current && distanceToBottom < 100) {
            getChat(pageRef.current + 1);
        }
    };

    useEffect(() => {
        if (!hasMore || fetchingRef.current || messages.length === 0) return;

        const el = bodyRef.current;
        if (el && el.scrollHeight <= el.clientHeight) {
            getChat(pageRef.current + 1);
        }
    }, [messages, hasMore]);

    useEffect(() => {
        if (isOpen && message) {
            setMessages([]);
            setHasMore(true);
            pageRef.current = 1;
            fetchingRef.current = false;
            getChat(1);
        }
    }, [isOpen, message]);

    return (
        <Modal isCentered isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <AnswerMessageModal
                getUse={getUse}
                isOpen={isOpenNew}
                message={messages[0]?.contact?.id}
                onCloseOld={onClose}
                onClose={onCloseNew}/>
            <ModalContent overflow="hidden" borderRadius="10px" minW="848px">
                <ModalHeader bg='#DD94FF' fontWeight="700" fontSize="24px" textAlign="center">
                    Message Thread
                </ModalHeader>

                <IconButton
                    height="unset"
                    onClick={onClose}
                    _hover={{ bg: "none" }}
                    _active={{ bg: "none" }}
                    bg="none"
                    aria-label="close button"
                    position="absolute"
                    top="1.5rem"
                    right="0.75rem"
                    icon={<Icon type="darkClose" />}
                />

                <ModalBody
                    p="30px"
                    overflow="auto"
                    maxH="600px"
                    bg="#F5F5F5"
                    ref={bodyRef}
                    onScroll={handleScroll}
                >
                    <Flex margin="auto" alignItems="center" flexDirection="column">
                        {messages.map((item, index) => (
                            <Flex
                                key={index}
                                justifyContent={item?.sender?.id === user?.id ? "end" : "start"}
                                width="100%"
                                mb={5}
                            >
                                <Box width="80%">
                                    <Flex
                                        alignItems="center"
                                        justifyContent={item?.sender?.id === user?.id ? "end" : "start"}
                                        mb={1}
                                        fontSize="14px"
                                        color="#2C628D"
                                        fontFamily="Titillium Web"
                                    >
                                        {item?.sender?.firstName + " " + item?.sender?.lastName}
                                        {/* Simplified Sender Label Logic */}
                                        {item?.sender?.id === user?.id && " (You)"}
                                        {item?.sender?.type === "Su" && " (Supplier)"}
                                        {item?.sender?.type === "Reu" && " (Real Estate)"}
                                        {item?.sender?.type === "Eu" && " (Electrician)"}
                                        {item?.sender?.type === "Sa" && " (Sales Admin)"}
                                        {item?.sender?.type === "Au" && " (Admin User)"}

                                        <Box width="6px" height="6px" bg="#A9A9A9" borderRadius="50%" mx={1} />
                                        {item?.sender?.email}
                                        <Box width="6px" height="6px" bg="#A9A9A9" borderRadius="50%" mx={1} />
                                        {moment(item?.createdAt?.date)?.format("MM/DD/YYYY")}
                                        {" (" + moment(item?.createdAt?.date).format("HH:mm a") + ")"}
                                    </Flex>
                                    <Box
                                        p="5px"
                                        bg={item?.sender?.id === user?.id ? "#F4FAFF" : "white"}
                                        border="1px solid #A9A9A9"
                                        borderRadius="5px"
                                    >
                                        {item?.message}
                                    </Box>
                                </Box>
                            </Flex>
                        ))}

                        <Button
                            type="submit"
                            mt={3}
                            width="254px"
                            onClick={onOpenNew}
                            variant="blue"
                            size="lg"
                            isLoading={loading}
                        >
                            Reply
                        </Button>
                        <Box minH="50px" />
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}
export default ChatModal